import { useCallback, useState } from "react";

/**
 * Ask before doing it, in the app's dialog.
 *
 * `window.confirm` cannot be styled, cannot be tested, and blocks the whole tab
 * — including the websocket handlers, so a native prompt open during a call
 * freezes everything behind it. There were six of them left in the tree when
 * the rule was written down, which made the rule a comment.
 *
 *   const { ask, confirmProps } = useConfirm();
 *   ask({ title: "Ștergi?", description: "…", destructive: true,
 *         confirmLabel: "Șterge", onConfirm: () => remove() });
 *   …
 *   <ConfirmDialog {...confirmProps} />
 *
 * The action runs only after the dialog is answered, and the dialog closes
 * whether the action resolved or threw — a dialog stuck open on an error is its
 * own bug report.
 */
export function useConfirm() {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = useCallback((options) => setPending(options), []);
  const close = useCallback(() => { if (!busy) setPending(null); }, [busy]);

  const onConfirm = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await pending.onConfirm?.();
    } finally {
      setBusy(false);
      setPending(null);
    }
  }, [pending]);

  return {
    ask,
    confirmProps: {
      open: !!pending,
      onOpenChange: close,
      busy,
      title: pending?.title || "",
      description: pending?.description || "",
      confirmLabel: pending?.confirmLabel,
      cancelLabel: pending?.cancelLabel,
      destructive: !!pending?.destructive,
      testId: pending?.testId || "confirm-dialog",
      onConfirm,
    },
  };
}

export default useConfirm;
